import React, { useState } from 'react';
import { X, Calculator, Thermometer, Scale, Clock, Droplets } from 'lucide-react';
import { SlowFermentationCalculator } from '../knowledge/calculators/SlowFermentationCalculator';
import { WaterTemperatureCalculator } from '../knowledge/calculators/WaterTemperatureCalculator';
import { StarterCalculator } from '../knowledge/calculators/StarterCalculator';
import { SessionTimingCalculator } from '../knowledge/calculators/SessionTimingCalculator';

interface CalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CalculatorModal({ isOpen, onClose }: CalculatorModalProps) {
  const [selectedCalculator, setSelectedCalculator] = useState<string | null>(null);

  const calculators = [
    {
      id: 'slow-fermentation',
      title: 'Slow Fermentation Starter Calculator',
      description: 'Calculate the right amount of starter for 18-hour room temperature fermentation',
      icon: Thermometer,
      component: SlowFermentationCalculator
    },
    {
      id: 'water-temperature',
      title: 'Water Temperature Calculator',
      description: 'Find the perfect water temperature for your desired dough temperature',
      icon: Droplets,
      component: WaterTemperatureCalculator
    },
    {
      id: 'starter-quantity',
      title: 'Starter Quantity Calculator',
      description: 'Calculate feeding amounts to get the exact starter quantity you need',
      icon: Scale,
      component: StarterCalculator
    },
    {
      id: 'session-timing',
      title: 'Session Timing Simulator',
      description: 'Plan your baking timeline with different fermentation processes',
      icon: Clock,
      component: SessionTimingCalculator
    }
  ];

  const handleCalculatorSelect = (calculatorId: string) => {
    setSelectedCalculator(calculatorId);
  };

  const handleBack = () => {
    setSelectedCalculator(null);
  };

  const renderContent = () => {
    if (selectedCalculator) {
      const calculator = calculators.find(c => c.id === selectedCalculator);
      if (!calculator) return null;
      
      const CalculatorComponent = calculator.component;
      return (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-8">
            <button 
              onClick={handleBack} 
              className="text-amber-400 hover:text-amber-300 transition-colors"
            >
              ← Back to Calculators
            </button>
          </div>
          <h2 className="text-2xl text-white font-brand font-light italic mb-4">{calculator.title}</h2>
          <CalculatorComponent />
        </div>
      );
    }

    return (
      <>
        <div className="mb-8">
          <p className="text-sm text-white/80">
            Professional tools to help you calculate hydration, timing, and feeding schedules for perfect sourdough.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {calculators.map(calculator => (
            <div 
              key={calculator.id}
              onClick={() => handleCalculatorSelect(calculator.id)}
              className="bg-gray-600/60 p-6 cursor-pointer hover:border hover:border-amber-400 transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <calculator.icon className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl text-white font-brand font-light italic mb-2">
                {calculator.title}
              </h3>
              <p className="text-sm text-white/80">
                {calculator.description}
              </p>
            </div>
          ))}
        </div>
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Calculator className="w-6 h-6 text-amber-400" />
              <h1 className="text-2xl text-white font-brand font-light italic">Sourdough Calculators</h1>
            </div>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}